import { useCallback } from "react";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";
import { useLenis } from "../providers/LenisProvider";

/**
 * Zwraca funkcję do podpięcia pod onClick linków nawigacji (#advantage, #sprint, ...).
 * Jeśli przeglądarka wspiera View Transitions API, scroll/zmiana stanu odbywa się
 * wewnątrz document.startViewTransition — daje to natywny crossfade zamiast
 * suchego "skoku". Jeśli API nie jest dostępne (Firefox, starsze Safari),
 * funkcja po prostu wykonuje zwykły scroll — pełny progressive enhancement,
 * żadna funkcjonalność się nie psuje.
 *
 * Respektuje prefers-reduced-motion: wtedy zawsze idzie ścieżką bez transition.
 *
 * Użycie:
 *   const navigateWithTransition = useViewTransitionNav();
 *   <a href="#sprint" onClick={(e) => { e.preventDefault(); navigateWithTransition("sprint"); }}>
 *     SPRINT
 *   </a>
 */
export function useViewTransitionNav() {
  const reducedMotion = usePrefersReducedMotion();
  const lenis = useLenis();

  return useCallback(
    (targetId: string) => {
      const target = document.getElementById(targetId);
      if (!target) return;

      const doScroll = () => {
        if (lenis) {
          lenis.scrollTo(target, { offset: 0 });
        } else {
          target.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
        }
      };

      const supportsViewTransitions =
        !reducedMotion && typeof document !== "undefined" && "startViewTransition" in document;

      if (supportsViewTransitions) {
        (document as Document & { startViewTransition: (cb: () => void) => void }).startViewTransition(() =>
          doScroll(),
        );
      } else {
        doScroll();
      }
    },
    [lenis, reducedMotion],
  );
}
